import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription
} from '../components/ui/alert-dialog';
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider
} from '../components/ui/tooltip';

export function PanicButton({ inHeader = false }) {
  const { panic } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [isPressing, setIsPressing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const HOLD_DURATION = 3000;

  const startPress = useCallback(() => {
    setIsPressing(true);
    setProgress(0);
    startTimeRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      const pct = Math.min(((Date.now() - startTimeRef.current) / HOLD_DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) { clearInterval(intervalRef.current); triggerPanic(); }
    }, 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endPress = useCallback(() => {
    setIsPressing(false);
    setProgress(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const triggerPanic = async () => {
    setShowDialog(true);
    setIsPressing(false);
    setProgress(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
    await panic();
    setTimeout(() => { setShowDialog(false); navigate('/'); }, 1500);
  };

  const radius = inHeader ? 14 : 24;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;
  const size = inHeader ? 36 : 56;
  const iconSize = inHeader ? 16 : 24;
  const svgSize = inHeader ? 36 : 56;

  const button = (
    <button
      data-testid="panic-button"
      className={`${inHeader
        ? 'relative w-9 h-9 rounded-xl bg-destructive/80 hover:bg-destructive text-destructive-foreground flex items-center justify-center'
        : 'fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-destructive text-destructive-foreground shadow-[0_18px_55px_rgba(0,0,0,.45)] flex items-center justify-center hover:brightness-110'
      } select-none touch-none active:brightness-95 focus-visible:ring-2 focus-visible:ring-ring`}
      onMouseDown={startPress}
      onMouseUp={endPress}
      onMouseLeave={endPress}
      onTouchStart={startPress}
      onTouchEnd={endPress}
      onTouchCancel={endPress}
      aria-label="Panik butonu - hesabı kilitlemek için 3 saniye basılı tutun"
    >
      {isPressing && (
        <svg
          data-testid="panic-button-progress"
          className="absolute inset-0 w-full h-full -rotate-90"
          viewBox={`0 0 ${svgSize} ${svgSize}`}
        >
          <circle
            cx={svgSize / 2} cy={svgSize / 2} r={radius}
            fill="none" stroke="hsl(38, 92%, 50%)" strokeWidth={inHeader ? 2 : 3}
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" style={{ transition: 'stroke-dashoffset 30ms linear' }}
          />
        </svg>
      )}
      <AlertTriangle className="relative z-10" style={{ width: iconSize, height: iconSize }} />
    </button>
  );

  return (
    <>
      {inHeader ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="bottom"><p className="text-xs">Panik Butonu (3sn basılı tut)</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : button}

      <AlertDialog open={showDialog}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Hesap Kilitlendi
            </AlertDialogTitle>
            <AlertDialogDescription>
              Hesabınız kilitlendi ve tüm oturumlarınız sonlandırıldı. Erişimi yeniden kazanmak için yöneticinize başvurun.
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
